import { usePresentation } from '@/hooks/Presentation';
import { useWorkspace } from '@/hooks/workspace';
import CollapsibleMenu from '@components/CollapsibleMenu';
import React, { useCallback, useEffect } from 'react';
import AutoSizer from 'react-virtualized-auto-sizer';

import { DashMenuOptionsProps, DashMenuProps } from './interfaces';
import {
  ChildrenContainer,
  ChildrenItem,
  CollapseButton,
  CollapseIcon,
  Container,
  List,
  ListItem,
  SupergroupContainer,
  SupergroupIcon,
  Title,
} from './styles';

const Main: React.FC<DashMenuProps> = ({
  options,
  dashMenuPath,
  useDashMenu,
  hiddenSupergroup,
  openSelectSuperGroup,
  isMobile,
}) => {
  const { workspaceData } = useWorkspace();
  const { setPath } = dashMenuPath;
  const { state, setState } = useDashMenu;

  const { isPresenting } = usePresentation();

  useEffect(() => {
    if (isPresenting) {
      setState({ ...state, show: false });
    }
  }, [isPresenting, setState]);

  const orderValues = useCallback((array): DashMenuOptionsProps[] => {
    return array.sort((a: DashMenuOptionsProps, b: DashMenuOptionsProps) => {
      return a.position - b.position;
    });
  }, []);

  const renderList = useCallback(() => {
    const listItems = orderValues(options).map((item) => {
      if (item.children) {
        return (
          <CollapsibleMenu
            wrapperStyle={{ hasBorderBottom: false }}
            key={item.id}
            isOpen={state.groupName === item.name}
            title={item.title ? item.title : item.name}
            onClick={(alreadyOpen) =>
              setState({ ...state, groupName: alreadyOpen ? '' : item.name })
            }
          >
            <ChildrenContainer>
              {orderValues(Object.values(item.children)).map((child) => {
                return (
                  <ChildrenItem
                    key={child.name}
                    onClick={() => {
                      setState({
                        ...state,
                        selected: {
                          name: child.name,
                          path: child.id,
                        },
                      });
                      setPath(child.path?.replace('/', ''));
                    }}
                    isSelected={
                      child.name === state.selected.name &&
                      child.id === state.selected.path
                    }
                  >
                    {child.title ? child.title : child.name}
                  </ChildrenItem>
                );
              })}
            </ChildrenContainer>
          </CollapsibleMenu>
        );
      }

      return (
        <ListItem
          key={item.name}
          disabled={item.disabled}
          onClick={() => {
            setState({
              ...state,
              selected: { name: item.name, path: item.id },
            });
            setPath(item.path?.replace('/', ''));
          }}
          isSelected={
            item.name === state.selected.name && item.id === state.selected.path
          }
        >
          {item.title ? item.title : item.name}
        </ListItem>
      );
    });

    return listItems;
  }, [options, setState, orderValues, setPath, state]);

  return (
    <Container className="dash-menu" show={state.show} isMobile={isMobile}>
      {!hiddenSupergroup && isMobile && !state.show && (
        <SupergroupContainer onClick={openSelectSuperGroup}>
          <SupergroupIcon />
        </SupergroupContainer>
      )}
      <CollapseButton
        disabled={isPresenting}
        onClick={() => setState({ ...state, show: !state.show })}
      >
        <CollapseIcon show={`${state.show}`} $isMobile={isMobile} />
      </CollapseButton>
      {state.show &&
        (!isMobile ? (
          <AutoSizer>
            {({ height, width }) => (
              <div style={{ height, width }}>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateRows: `50px ${height - 50}px`,
                  }}
                >
                  <Title>
                    {workspaceData.name === 'SESRJ'
                      ? 'Acompanhamento Assistencial'
                      : 'Dashboard'}
                  </Title>
                  <div
                    style={{
                      maxHeight: '100%',
                      overflow: 'hidden auto',
                      marginTop: workspaceData.name === 'SESRJ' ? 20 : 0,
                    }}
                  >
                    <List>{renderList()}</List>
                  </div>
                </div>
              </div>
            )}
          </AutoSizer>
        ) : (
          <>
            <Title>
              {workspaceData.name === 'SESRJ'
                ? 'Acompanhamento Assistencial'
                : 'Dashboard'}
            </Title>
            <List>{renderList()}</List>
          </>
        ))}
    </Container>
  );
};

export default Main;
